// SPEC-COLLECTION-OFFLINE-COMPLETE-01 (D-268) B1: 集金ドラフト store の CRUD 基盤。
//   PRIOR_ART = src/lib/localStore/patrolRecords.js の設計原則をそのまま踏襲する(ゼロから作らない):
//   1レコード1トランザクション / synced は文字列インデックス('true'|'false') / localId=UUID。
//   D-268a の担当は B1(store 追加)+ PHASE_A の A5(未送信ドラフトの読み取り)。
//   D-268e(PHASE_B B2-B10 + FIX_B11-B14): 追記専用の訂正(B2)・冪等運用(B3)・送信(B8)・7日掃除(B13)の
//     ライフサイクル helper を追加。送信オーケストレーションは collectionSync.js(patrol の storeSync.js と同設計)。
//   B2 追記専用: putDraft は localId ごとに put する。訂正は「新しい localId」で追記する(既存 payload を上書きしない)。
//     synced フラグの更新(markDraftSynced=B8)と 7日超の synced 掃除(cleanupSyncedDrafts=B13)は
//     patrol の markRecordSynced と同じく「payload を壊さないライフサイクル遷移」であり B2 の禁止対象ではない。
import { getDb, STORE_COLLECTION_DRAFTS } from './db'
import { logger } from '../logger'

const LOG_ERR_WRITE = 'ERR-LF1-IDB-WRITE'
const LOG_ERR_READ  = 'ERR-LF1-IDB-READ'
const LOG_ERR_QUOTA = 'ERR-LF1-IDB-QUOTA' // B6: 容量不足は既存 ERR-LF1-IDB-QUOTA と同じ扱い

function syncedKey(b) { return b ? 'true' : 'false' }

export class IdbDraftWriteError extends Error {
  constructor(code, cause) {
    super(cause?.message ?? code)
    this.code = code
    this.cause = cause
  }
}

/**
 * 集金ドラフトの atomic put(1レコード1トランザクション)。B1 基盤。
 * record shape(最小 subset):
 *  - localId          (uuid, IDB key。無ければ生成)
 *  - store_code       (text, index)
 *  - idempotency_key  (uuid, D-267 fn_save_collection の再送検知キー)
 *  - staff_id / staff_name  (B4: 誰が入れたかはレコードが持つ。端末混在のため)
 *  - collection       (親行 payload) / booths (明細 payload)  ※送信は PHASE_B
 *  - synced           (boolean。既定 false)
 *  - syncedKey        (syncedKey(synced))
 * QuotaExceededError は必ず catch し ERR-LF1-IDB-QUOTA を投げる(B6 相当。呼出側でユーザー警告)。
 */
// ─── SPEC-COLLECTION-DRAFT-BLOB-ARRAYBUFFER-V1 (D-757) レシートは ArrayBuffer で持つ ───────────
//
// ★実測(2026-09-15 / Playwright の WebKit): IndexedDB に Blob を入れると
//   `UnknownError: Error preparing Blob/File data to be stored in object store` で落ちる。
//   ★`new Blob(['hello'])` すら通らない = 私たちのデータの問題ではなく、そのビルドでは
//     Blob を IDB に入れる機能そのものが動いていない。ArrayBuffer なら通る。
//   ★そのせいで「レシートを撮ってから確定する」経路を **本番のエンジン(WebKit)で
//     1 度も検査できない** 状態だった。検査できない道は、壊れても誰も気づかない。
//
// ★★ただし **本番で壊れているという前提は立っていない**(D-757 の調査):
//   ・通常集金は同じ putDraft に 3 秒ごとの自動保存で Blob を書いており、実機で毎日動いている。
//     実機 Safari で入らないなら、レシートを撮った集金は毎回 ERR-COLLECTION-011 で止まるはず。
//   ・Sentry 90 日で Blob / ERR-COLLECTION-011 は 0 件。
//   → 直す動機は「本番の障害を止める」ではなく「WebKit で検査できるようにする」こと。
//
// ★形の約束:
//   ・書くとき(putDraft)だけ直列化する。
//   ・生の読み手 3 つ(getDraftById / getDraftsByStore / getAllUnsyncedDrafts)だけが復元する。
//     派生の読み手はこの 3 つを通るので自動的に直る。
//   ・★内部の read-modify-write(markDraftSynced 等)は素通し。あれらは tx.store.put で
//     生のまま書き戻すので、復元すると Blob に戻して書き込み、直そうとしている状態を再生産する。
//   ・★呼び出し側は 1 行も変えない。collectionSync も 2 画面も今までどおり Blob を受け取る。
//   ・★既に Blob として入っている端末のレコードはそのまま返す(一括移行はしない)。
//     移行の途中で落ちたら未送信の集金=根拠が消える。読むときに両方の形を受ける方が安全。
const BLOB_REF = '__blobRef'

function isBlobLike(v) {
  return typeof Blob !== 'undefined' && v instanceof Blob
}
function isBlobRef(v) {
  return !!v && typeof v === 'object' && v[BLOB_REF] === 1 && v.buffer != null
}

/** Blob → 保存できる形。★type を落とさない(Storage の Content-Type が変わる)。 */
async function toBlobRef(blob) {
  return { [BLOB_REF]: 1, type: blob.type || '', size: blob.size, buffer: await blob.arrayBuffer() }
}
/** 保存できる形 → Blob。 */
function fromBlobRef(ref) {
  return new Blob([ref.buffer], { type: ref.type || '' })
}

/**
 * D-757: 下書きの中のレシートを保存できる形にする。★見るのは receipts と bulkReceipt の 2 か所だけ。
 *   ★signatureDataUrl は dataURL(文字列)なので対象外。
 */
export async function serializeDraftBlobs(record) {
  if (!record || typeof record !== 'object') return record
  const out = { ...record }
  if (isBlobLike(out.bulkReceipt)) out.bulkReceipt = await toBlobRef(out.bulkReceipt)
  if (out.receipts && typeof out.receipts === 'object') {
    const receipts = {}
    for (const [k, v] of Object.entries(out.receipts)) {
      receipts[k] = isBlobLike(v) ? await toBlobRef(v) : v
    }
    out.receipts = receipts
  }
  return out
}

/** D-757: 読み出した下書きのレシートを Blob に戻す。★Blob のまま入っている古い形は素通し(AC3)。 */
export function reviveDraftBlobs(record) {
  if (!record || typeof record !== 'object') return record
  const out = { ...record }
  if (isBlobRef(out.bulkReceipt)) out.bulkReceipt = fromBlobRef(out.bulkReceipt)
  if (out.receipts && typeof out.receipts === 'object') {
    const receipts = {}
    for (const [k, v] of Object.entries(out.receipts)) {
      receipts[k] = isBlobRef(v) ? fromBlobRef(v) : v
    }
    out.receipts = receipts
  }
  return out
}

const reviveAll = (list) => (Array.isArray(list) ? list.map(reviveDraftBlobs) : [])

export async function putDraft(record) {
  try {
    const db = await getDb()
    const now = new Date().toISOString()
    const localId = record.localId ?? (crypto.randomUUID?.() ?? `cd-${Date.now()}-${Math.random().toString(36).slice(2)}`)
    const synced = record.synced ?? false
    const merged = {
      ...record,
      localId,
      synced,
      syncedKey: syncedKey(synced),
      createdLocally: record.createdLocally ?? now,
      updatedLocally: now,
    }
    // D-757: レシート(Blob)は ArrayBuffer にしてから書く。★返り値は Blob のまま
    //   (呼び出し側が受け取る形を変えない。保存の形の話を業務の側へ漏らさない)。
    const stored = await serializeDraftBlobs(merged)
    const tx = db.transaction(STORE_COLLECTION_DRAFTS, 'readwrite')
    await tx.store.put(stored)
    await tx.done
    return merged
  } catch (err) {
    const tag = err?.name === 'QuotaExceededError' ? LOG_ERR_QUOTA : LOG_ERR_WRITE
    logger.error?.(tag, { storeCode: record?.store_code, message: err?.message })
    throw new IdbDraftWriteError(tag, err)
  }
}

export async function getDraftById(localId) {
  if (!localId) return null
  try {
    const db = await getDb()
    return reviveDraftBlobs(await db.get(STORE_COLLECTION_DRAFTS, localId)) // D-757
  } catch (err) {
    logger.error?.(LOG_ERR_READ, { localId, message: err?.message })
    return null
  }
}

export async function getDraftsByStore(storeCode) {
  if (!storeCode) return []
  try {
    const db = await getDb()
    return reviveAll(await db.getAllFromIndex(STORE_COLLECTION_DRAFTS, 'byStoreCode', storeCode)) // D-757
  } catch (err) {
    logger.error?.(LOG_ERR_READ, { storeCode, message: err?.message })
    return []
  }
}

// PHASE_A A5: その店に未送信の集金ドラフトがあるとき、前回集金メーターにローカル値を反映するための読み取り。
//   層1(この端末のローカル未送信)は業務で読んでよい唯一の層。synced='false' のみ返す。
export async function getUnsyncedDraftsByStore(storeCode) {
  const all = await getDraftsByStore(storeCode)
  return all.filter(d => d && d.synced !== true)
}

export async function getAllUnsyncedDrafts() {
  try {
    const db = await getDb()
    return reviveAll(await db.getAllFromIndex(STORE_COLLECTION_DRAFTS, 'bySynced', syncedKey(false))) // D-757
  } catch (err) {
    logger.error?.(LOG_ERR_READ, { message: err?.message })
    return []
  }
}

// B12: 送信対象は「署名確定まで到達した完了ドラフト(status==='complete')の未送信」のみ。
//   入力途中(status!=='complete')はローカルに残すが送信しない・未送信件数に数えない。
//   ※本 D-268e では confirm() 時にしか putDraft しない(B10 で入力中の自動保存を作らない)ため、
//     実運用のドラフトは全て complete だが、送信フィルタは status で明示ガードする(将来 draft 保存が入っても汚さない)。
export function isSendableDraft(d) {
  return !!d && d.synced !== true && d.status === 'complete'
}

export async function getCompletedUnsyncedDrafts() {
  const unsynced = await getAllUnsyncedDrafts()
  return unsynced.filter(isSendableDraft)
}

// 未送信件数 + 関与 store 数(PHASE_D の可視化で使う想定。B12: 未送信=送るべきなのに送れていない=完了未送信のみ数える)。
export async function getUnsyncedDraftSummary() {
  const sendable = await getCompletedUnsyncedDrafts()
  const storeSet = new Set()
  for (const d of sendable) if (d.store_code) storeSet.add(d.store_code)
  return { count: sendable.length, storeCount: storeSet.size, drafts: sendable }
}

// D-982c (gate_3 差し戻し是正): 更新通知の未送信ガード専用、厳格(strict)な件数読み取り。
//   ★なぜ要るか: getAllUnsyncedDrafts は IDB 例外を catch して [] を返す(= fail-open)。
//   getUnsyncedDraftSummary もそれを経由するため同じく fail-open になる。更新通知側がこれらを
//   呼ぶと「読み取り失敗」と「本当に未送信ゼロ」を区別できず、確認なしで更新が適用されてしまう
//   (gate_3 (a) 指摘)。
//   ★既存関数(getAllUnsyncedDrafts/getCompletedUnsyncedDrafts/getUnsyncedDraftSummary)の実装・
//   戻り値・catch は 1 バイトも変えていない。既存の呼び出し元への影響はゼロ。
//   ★この関数は読み取りのみ(getDb + getAllFromIndex + reviveAll + isSendableDraft フィルタ)。
//   書き込み・削除・Sentry 送信は一切しない。catch を持たないため、IDB 障害時は呼び出し元まで
//   例外がそのまま伝播する(意図的な fail-closed)。
export async function countUnsyncedDraftsStrict() {
  const db = await getDb()
  const all = reviveAll(await db.getAllFromIndex(STORE_COLLECTION_DRAFTS, 'bySynced', syncedKey(false)))
  const sendable = all.filter(isSendableDraft)
  const storeSet = new Set()
  for (const d of sendable) if (d.store_code) storeSet.add(d.store_code)
  return { count: sendable.length, storeCount: storeSet.size }
}

// PHASE_D D2/決定7(純関数): 完了未送信を「自分」と「この端末の他人」に分け、店舗数と最古の作成時刻(ms)も返す。
//   誰が入れたかはレコードの staff_id で判定する(端末混在=決定4/6)。金額・署名・個人名は一切読まない/返さない。
export function splitUnsentByStaff(drafts, myStaffId) {
  const complete = (drafts ?? []).filter(d => d && d.synced !== true && d.status === 'complete')
  const me = String(myStaffId ?? '')
  let mine = 0, others = 0, oldestMs = null
  const storeSet = new Set()
  for (const d of complete) {
    if (String(d.staff_id ?? '') === me) mine++; else others++
    if (d.store_code) storeSet.add(d.store_code)
    const t = Date.parse(d.createdLocally ?? '')
    if (Number.isFinite(t) && (oldestMs == null || t < oldestMs)) oldestMs = t
  }
  return { mine, others, total: complete.length, storeCount: storeSet.size, oldestMs }
}

// PHASE_D D3/FIX_D6(純関数): 最古の完了未送信が閾値(既定24h)を超えていれば、警報1件分の情報を返す。
//   FIX_D6: 同一 localId は閾値内に1回だけ(lastAlarmMap[localId]=最終警報 ms で dedup)。件数が増えて最古が別 localId に
//   替われば別途1回上げてよい。★payload は count / oldestAgeMs / store_code のみ(金額・署名・個人名は絶対含めない=D3)。
export function pickStaleAlarm(drafts, lastAlarmMap, nowMs, thresholdMs = 24 * 60 * 60 * 1000) {
  const complete = (drafts ?? []).filter(d => d && d.synced !== true && d.status === 'complete')
  if (!complete.length) return null
  let oldest = null, oldestT = Infinity
  for (const d of complete) {
    const t = Date.parse(d.createdLocally ?? '')
    if (Number.isFinite(t) && t < oldestT) { oldestT = t; oldest = d }
  }
  if (!oldest) return null
  const ageMs = nowMs - oldestT
  if (ageMs < thresholdMs) return null
  const last = lastAlarmMap?.[oldest.localId]
  if (Number.isFinite(last) && (nowMs - last) < thresholdMs) return null // 同一 localId は閾値内1回だけ
  return { localId: oldest.localId, payload: { count: complete.length, oldestAgeMs: ageMs, store_code: oldest.store_code ?? null } }
}

// B8: サーバ成功応答を受け取ってから synced を立てる唯一の関数(payload は触らず synced/syncedAt のみ)。
//   savedCollectionId を控えとして残す(B13 掃除の監査・突合用)。
export async function markDraftSynced(localId, savedCollectionId = null) {
  if (!localId) return null
  try {
    const db = await getDb()
    const tx = db.transaction(STORE_COLLECTION_DRAFTS, 'readwrite')
    const existing = await tx.store.get(localId)
    if (!existing) { await tx.done; return null }
    const next = {
      ...existing,
      synced: true,
      syncedKey: syncedKey(true),
      syncedAt: new Date().toISOString(),
      savedCollectionId: savedCollectionId ?? existing.savedCollectionId ?? null,
    }
    await tx.store.put(next)
    await tx.done
    return next
  } catch (err) {
    logger.error?.(LOG_ERR_WRITE, { localId, phase: 'mark-synced', message: err?.message })
    return null
  }
}

// FIX_B16: 送信時に採番した collection_id をドラフトへ1回だけ焼き付ける。再送では読み出して使い回し、
//   採番し直さない(=毎回新 collection_id 配下に署名を上げて孤児を量産する NG7 を断つ)。payload は触らない。
export async function setDraftCollectionId(localId, collectionId) {
  if (!localId || !collectionId) return null
  try {
    const db = await getDb()
    const tx = db.transaction(STORE_COLLECTION_DRAFTS, 'readwrite')
    const existing = await tx.store.get(localId)
    if (!existing) { await tx.done; return null }
    if (existing.collection_id) { await tx.done; return existing } // 既に確定済みなら維持(再採番しない)
    const next = { ...existing, collection_id: collectionId }
    await tx.store.put(next)
    await tx.done
    return next
  } catch (err) {
    logger.error?.(LOG_ERR_WRITE, { localId, phase: 'set-collection-id', message: err?.message })
    return null
  }
}

// D-268h FIX_A5_DATE_GUARD 用: 値を JST 暦日('YYYY-MM-DD')へ正規化する。既に日付のみ文字列ならそのまま(暦日として扱う)。
//   タイムスタンプは sv-SE + Asia/Tokyo で JST 暦日へ(toISOString().split/slice は FORBIDDEN なので使わない)。不正値は null。
function jstCalendarDate(v) {
  if (v == null || v === '') return null
  const s = String(v)
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s // 日付のみ='YYYY-MM-DD' はそのまま暦日
  const d = new Date(s)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleDateString('sv-SE', { timeZone: 'Asia/Tokyo' })
}

// FIX_A5_WIRE/FIX_A5_PER_BOOTH/FIX_A5_DATE_GUARD(純関数): その店のローカル未送信(完了)ドラフトから
//   booth_code → in_meter_current(数値)の map を返す。前回IN にローカル未送信値を反映するための土台(層1のみ読む)。
//   ★D-268g NG9: 店舗単位で最新1件だけを見ると、最新ドラフトで空欄のブースが古い有効値を無視してサーバ古値に戻る。
//     → collected_at 昇順に**全ドラフト**を走査し、各ブースは最も新しい有効値が残る(空欄=非数値は古い値を消さない)。
//   ★D-268h NG12(後入力ガード): baselineDate(入力中の collectedAt)を渡すと、collected_at が基準日より**厳密に古い**
//     完了未送信ドラフトだけを対象にする。同日は対象外(安全側)。判定は JST 暦日(文字列比較でなく暦日正規化)で行う。
//     8/9 に集金して未送信のまま後から 8/5 を入力しても、8/9 の値が 8/5 の前回IN に入り時間が逆流する事故を防ぐ(D-254 FIX_01 と同型)。
export function latestUnsentPrevMeterMap(drafts, baselineDate = null) {
  let complete = (drafts ?? []).filter(d => d && d.synced !== true && d.status === 'complete')
  if (baselineDate != null) {
    const base = jstCalendarDate(baselineDate)
    if (base == null) return {} // 基準日が不正なら安全側で何も反映しない
    complete = complete.filter(d => {
      const dd = jstCalendarDate(d.collected_at)
      return dd != null && dd < base // 厳密に古いものだけ(同日=除外)
    })
  }
  if (!complete.length) return {}
  complete.sort((x, y) => String(x.collected_at ?? '').localeCompare(String(y.collected_at ?? ''))) // 昇順(後勝ち=新しい方が残る)
  const map = {}
  for (const d of complete) {
    for (const [boothCode, row] of Object.entries(d.payload ?? {})) {
      const cur = row?.in_meter_current
      const n = (cur === '' || cur == null) ? null : Number(cur)
      if (n != null && Number.isFinite(n)) map[boothCode] = n // 有効値のみ上書き=空欄は前の値を消さない(ブース単位で最新)
    }
  }
  return map
}

// FIX_A5_ORDER/FIX_A5_UNTOUCHED_ONLY(純関数): rowData に対して層1(ローカル未送信)の前回IN を上書きし、実際に反映できた件数を返す。
//   ・層1 = この端末で確定した最新の事実 → in_meter_prev はサーバ値/prevDate 値より優先(handleLoad の新規構築経路では無条件上書き)。
//   ・★D-268h NG11: 集金日変更経路では入力途中の rowData に対して走るため、クレサポが**手で直した前回IN**を消してはならない。
//     onlyUntouched=true のとき、in_meter_prev が目印 _prefilled_prev と一致(=システムが置いたまま未編集)のブースだけ上書きする。
//     呼び分けは引数で**明示**する(関数内で呼び出し元を推測しない)。書き込み時は _prefilled_prev も同値へ更新(次回の未編集判定を維持)。
//   ・applied は「rowData に実在するブースへ実際に入った件数」= 表示フラグ(unsentApplied)は「やろうとした」でなく「最終的に入った」を根拠に(NG8)。
export function applyUnsentPrevToRowData(rowData, unsentMap, { onlyUntouched = false } = {}) {
  const next = { ...(rowData ?? {}) }
  let applied = 0
  for (const [boothCode, val] of Object.entries(unsentMap ?? {})) {
    const row = next[boothCode]
    if (!row) continue
    if (onlyUntouched && (row.in_meter_prev ?? '') !== (row._prefilled_prev ?? '')) continue // 手入力は残す
    const s = String(val)
    next[boothCode] = { ...row, in_meter_prev: s, _prefilled_prev: s }
    applied++
  }
  return { rowData: next, applied }
}

// 送信失敗時のみ lastErrCode を残す(未送信のまま=再送対象)。synced は変えない(patrol setRecordLastErrCode と同思想)。
export async function setDraftLastError(localId, errCode) {
  if (!localId) return null
  try {
    const db = await getDb()
    const tx = db.transaction(STORE_COLLECTION_DRAFTS, 'readwrite')
    const existing = await tx.store.get(localId)
    if (!existing) { await tx.done; return null }
    const next = { ...existing, lastErrCode: errCode ?? null, lastErrAt: new Date().toISOString() }
    await tx.store.put(next)
    await tx.done
    return next
  } catch (err) {
    logger.error?.(LOG_ERR_WRITE, { localId, phase: 'last-err', message: err?.message })
    return null
  }
}

// SPEC-COLLECTION-INPUT-AUTOSAVE-DRAFT-01 (D-282) ITEM_2: 入力途中(status==='draft')の未送信レコードを1件取得する。
//   復元ダイアログの候補探索に使う。保持する draft は常に1件だけ(別店舗ロード時に破棄)なので通常0/1件だが、
//   防御的に updatedLocally 降順で最新を返す。complete(送信対象)や synced 済は対象外(status ガードは既存どおり)。
/**
 * SPEC-COLLECTION-BULK-AUTOSAVE-01 (D-691): そのドラフトがどちらの数え方のものか。
 *   ★counting_mode が無い古いレコードは通常入力のものとして扱う(後方互換)。
 *     D-689 より前に作られた未送信ドラフトには列そのものが無い。
 *   ★ここを 1 か所に置く。判定を呼び出し側に書くと必ずずれる(D-616)。
 */
export function draftCountingMode(d) {
  return d?.counting_mode === 'bulk' ? 'bulk' : 'itemized'
}

/**
 * SPEC-COLLECTION-DUPLICATE-SUBMIT-GUARD-01 (D-803) AC1:
 *   **この端末に既にある「同じ集金」の下書き** を 1 件返す。
 *
 * ★★なぜ要るのか(2026-09-18 本番実測):
 *   ドンキ那珂川 9/11 に集金が **4 件**、ドンキ別府 8/31 に **3 件** 入っていた(D-802 で 5 件 cancelled)。
 *   ★7 件すべて idempotency_key の値が **違っていた**(重複率 0/7)。
 *     = fn_save_collection の冪等リプレイが **一度も発火していない**。
 *   ★fn_save_collection 側にバグは無い。穴は **キーの粒度** にあった:
 *     idempotencyKeyRef は「画面を mount した瞬間に 1 回」生成していたので、
 *     ★一度画面を閉じて開き直す(リロード / タブを閉じる / アプリ再起動)と新しいキーになり、
 *       サーバからは「同じ集金の再送」ではなく **「新しい集金」** に見えていた。
 *   ★実測の間隔(那珂川 1 時間 43 分 → 6 分 → 7 分 / 別府 14 分 → 4 分)は連打ではなく、
 *     一度画面を離れて戻ってきた形に一致する。
 *
 * ★WORLD_STANDARD(Stripe / OneSignal / Telnyx 等の冪等キー設計):
 *   「1 つの **業務オペレーション** に 1 つのキー。session や page load ごとに作り直さない。
 *     再送はキーを再利用する。プロセスが落ちた後の再送でも元のキーを使えるよう永続化する」
 *   ★mount は UI の寿命であって、業務(この店・この日の集金)の寿命ではない。
 *
 * ★だから業務の単位 = **(store_code, collected_at, counting_mode)** で引く。
 *   ★synced / status は問わない(送信済みでも「同じ集金」なら同じキーを使う。
 *     それが冪等リプレイが効く唯一の形)。
 *   ★counting_mode でも絞る。保管庫は通常入力と一括集金で共用なので、
 *     絞らないと別の数え方の下書きのキーを引き継いでしまう(D-691 と同じ理由)。
 *   ★複数あれば updatedLocally が一番新しいものを返す。
 *
 * ★守れる範囲は **同じ端末の中だけ**。別端末や IndexedDB を消した後は守れない。
 *   → そこは D-803 AC2/AC3/AC4 のサーバ照会(先方サインの直前に確認を出す)で補う。
 *
 * @param {string} storeCode
 * @param {string} collectedAt  'YYYY-MM-DD'(または日付に解釈できる値)
 * @param {'itemized'|'bulk'} countingMode
 */
export async function findDraftForCollection(storeCode, collectedAt, countingMode = 'itemized') {
  if (!storeCode || !collectedAt) return null
  const want = jstCalendarDate(collectedAt)
  if (want == null) return null
  const mode = countingMode === 'bulk' ? 'bulk' : 'itemized'
  const all = await getDraftsByStore(storeCode)
  const hit = (all ?? []).filter(d =>
    d
    && jstCalendarDate(d.collected_at) === want
    && draftCountingMode(d) === mode,
  )
  if (!hit.length) return null
  // 新しい順。★updatedLocally はどのレコードにも入る(putDraft が必ず打つ)。
  hit.sort((a, b) => String(b.updatedLocally ?? '').localeCompare(String(a.updatedLocally ?? '')))
  return hit[0]
}

/**
 * 入力途中(status='draft')の復元候補。
 *   SPEC-COLLECTION-BULK-AUTOSAVE-01 (D-691): ★数え方で絞る。
 *   ★保管庫は通常入力と一括集金で共用。絞らないと
 *     一括集金の下書きが通常入力の復元ダイアログに出る(逆も同じ)。
 *     両者は金額の持ち方が違う(通常 = counts の金種枚数 / 一括 = amount の直入力 + 店舗単位の bulk_counts)
 *     ので、取り違えて復元すると金額が壊れる。
 *   ★既定を 'itemized' にしてあるので、通常入力側(引数なしで呼ぶ)は 1 行も変えずに済む。
 *     見え方の変化は「一括集金の下書きがもう出てこない」だけで、それが本 D の目的。
 */
export async function getLatestInputDraft({ countingMode = 'itemized' } = {}) {
  const unsynced = await getAllUnsyncedDrafts()
  const drafts = (unsynced ?? [])
    .filter(d => d && d.status === 'draft' && draftCountingMode(d) === countingMode)
  if (!drafts.length) return null
  drafts.sort((a, b) => String(b.updatedLocally ?? '').localeCompare(String(a.updatedLocally ?? '')))
  return drafts[0]
}

// D-282 ITEM_2/ITEM_3: 指定 localId のドラフトを1件削除する(復元「破棄」/別店舗「破棄して読み込む」用)。
//   単一 localId のピンポイント削除。complete/draft の区別はせず呼び出し側の意思で消す(呼ぶのは draft のみ)。
export async function deleteDraft(localId) {
  if (!localId) return false
  try {
    const db = await getDb()
    const tx = db.transaction(STORE_COLLECTION_DRAFTS, 'readwrite')
    await tx.store.delete(localId)
    await tx.done
    return true
  } catch (err) {
    logger.error?.(LOG_ERR_WRITE, { localId, phase: 'delete-draft', message: err?.message })
    return false
  }
}

// D-282 ITEM_5: 入力途中(status==='draft')で updatedLocally が maxAgeDays を超えたものだけを掃除する。
//   ★status==='complete'(未送信=送信箱の中身)は経過日数に関係なく絶対に消さない(B13 の保証を崩さない)。
//   既存 cleanupSyncedDrafts(synced=true のみ)とは別物・別関数。draft は常に synced=false なので bySynced 'false' を走査。
export const DRAFT_MAX_AGE_DAYS = 7 // 3秒(自動保存debounce)と並ぶ調整点。iOS の未使用ストレージ削除期間と揃える。
export async function cleanupStaleInputDrafts({ nowMs = Date.now(), maxAgeDays = DRAFT_MAX_AGE_DAYS } = {}) {
  try {
    const db = await getDb()
    const unsynced = await db.getAllFromIndex(STORE_COLLECTION_DRAFTS, 'bySynced', syncedKey(false))
    if (!unsynced.length) return 0
    const cutoff = nowMs - maxAgeDays * MS_PER_DAY
    const stale = unsynced.filter(d => {
      if (!d || d.status !== 'draft') return false // complete は絶対対象外(経過日数無関係)
      const t = Date.parse(d.updatedLocally ?? '')
      return Number.isFinite(t) && t <= cutoff
    })
    if (!stale.length) return 0
    const tx = db.transaction(STORE_COLLECTION_DRAFTS, 'readwrite')
    let deleted = 0
    for (const d of stale) { await tx.store.delete(d.localId); deleted++ }
    await tx.done
    if (deleted > 0) logger.info?.('LF1_COLLECTION_INPUT_DRAFT_CLEANUP', { deleted })
    return deleted
  } catch (err) {
    logger.error?.(LOG_ERR_WRITE, { phase: 'input-draft-cleanup', message: err?.message })
    return 0
  }
}

// B13: 送信成功(synced=true)して 7 日経過したドラフトを署名画像/写真 Blob ごと削除する。
//   ・未送信(synced!==true)は経過日数に関係なく絶対に削除しない(送信箱の中身を失わせない)。
//   ・7 日 = iOS が未使用時にストレージを消す期間と揃える。層2 の控え(ひろ決定2)は別物でこの掃除の対象外。
//   nowMs は呼び出し側から渡せる(テスト用)。既定は現在時刻。
const MS_PER_DAY = 24 * 60 * 60 * 1000
export async function cleanupSyncedDrafts({ nowMs = Date.now(), maxAgeDays = 7 } = {}) {
  try {
    const db = await getDb()
    // synced=true のみ走査(未送信は index 'true' に載らないので構造的に対象外=B13 の絶対条件を index で担保)。
    const synced = await db.getAllFromIndex(STORE_COLLECTION_DRAFTS, 'bySynced', syncedKey(true))
    if (!synced.length) return 0
    const cutoff = nowMs - maxAgeDays * MS_PER_DAY
    const stale = synced.filter(d => {
      const t = Date.parse(d.syncedAt ?? d.updatedLocally ?? '')
      return Number.isFinite(t) && t <= cutoff
    })
    if (!stale.length) return 0
    const tx = db.transaction(STORE_COLLECTION_DRAFTS, 'readwrite')
    let deleted = 0
    for (const d of stale) { await tx.store.delete(d.localId); deleted++ }
    await tx.done
    if (deleted > 0) logger.info?.('LF1_COLLECTION_DRAFT_CLEANUP', { deleted })
    return deleted
  } catch (err) {
    logger.error?.(LOG_ERR_WRITE, { phase: 'draft-cleanup', message: err?.message })
    return 0
  }
}
